import { AccordionItem } from '@e3dc-react/shell/Elements/Accordion'
import React, { useCallback, useState } from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box } from '@mui/material'
import SettingsFormContainer from 'src/Elements/SystemSettings/forms/SettingsFormContainer'
import { Formik } from 'formik'
import DailyIdlePeriodFieldSet from 'src/Libs/smart-charge/components/ems-idle-period-legacy/DailyIdlePeriodFieldSet'
import { IdlePeriodLegacySettingsFormModel, IdlePeriodLegacySettingsModel } from 'src/Libs/smart-charge/models'
import { fromLegacyIdlePeriodModelToForm } from 'src/Libs/smart-charge/transformers'
import useStyles from 'src/Libs/styles'
import IdlePeriodToggleAllAction from 'src/Libs/smart-charge/components/ems-idle-period-legacy/IdlePeriodToggleAllAction'

export interface Props {
  model: IdlePeriodLegacySettingsModel
  onChange: (values: IdlePeriodLegacySettingsFormModel) => Promise<void>
  hasWritingRights?: boolean
}

/**
 * This implementation for the IdlePeriods is using a legacy data schema, which still need to be supported
 * @param model
 * @param onChange
 * @constructor
 */
const EmsIdlePeriodsLegacySection: React.FC<Props> = ({ model, onChange, hasWritingRights = true }) => {
  const { t } = useCustomTranslation()
  const [expanded, setExpanded] = useState<boolean>(false)
  const [formValues, setFormValues] = useState(fromLegacyIdlePeriodModelToForm(model))
  const { classes } = useStyles()

  const callback = useCallback(() => {
    setExpanded(!expanded)
  }, [expanded])

  const handleSubmit = async (values: IdlePeriodLegacySettingsFormModel): Promise<void> => {
    try {
      await onChange(values)
    } catch (error) {
      /* revert changes */
      setFormValues({ ...formValues })
    }
  }

  return (
    <Formik enableReinitialize={true} initialValues={formValues} onSubmit={handleSubmit}>
      {({ values }) => {
        const periodFields = values.emsIdlePeriod.map((periodsByDay, index) => (
          <DailyIdlePeriodFieldSet
            key={index}
            title={t(`common.weekdays.byIndex${periodsByDay.dayOfWeek}`)}
            name="emsIdlePeriod"
            index={index}
            titleForDischarge={t('systemSettings.smartFunctions.smartCharge.emsIdlePeriods.sections.discharge.title')}
            titleForCharge={t('systemSettings.smartFunctions.smartCharge.emsIdlePeriods.sections.charge.title')}
            hasWritingRights={hasWritingRights}
          />
        ))

        return (
          <AccordionItem
            expanded={expanded}
            callback={callback}
            title={t('systemSettings.smartFunctions.smartCharge.emsIdlePeriods.sectionTitle')}
            className={classes.accordion}
            headerChildren={<IdlePeriodToggleAllAction fieldName={'emsIdlePeriod'} disabled={!expanded || !hasWritingRights} />}
          >
            <Box sx={{ pl: { md: 5 }, pr: { md: 5 } }}>
              <SettingsFormContainer model={formValues}>{periodFields}</SettingsFormContainer>
            </Box>
          </AccordionItem>
        )
      }}
    </Formik>
  )
}

export default EmsIdlePeriodsLegacySection
